import { internalFetch } from './fetchHelper';
import { getBackendApiUrl, getBackendApiKey } from './runtimeEnv';
import type { IPagedCollection } from './interfaces/IPagedCollection';

export interface SiteAvailabilityDraft {
  id?: number;
  date: string;
  startTime: string;
  endTime: string;
  timezoneId: string;
  duration: number;
  priority: 'None' | 'Preferred' | 'SecondPreference';
  status: boolean;
  sourceControlled: string;
}
export interface OrderSiteAvailabilityRequest {
  orderId?: number;
  startAt: string;
  endAt: string;
  timezoneId: string;
  duration: number;
  priority: SiteAvailabilityDraft['priority'];
  status: boolean;
  sourceControlled: string;
  isAllDay: boolean;
}
export interface OrderSiteAvailabilityWindow
  extends Omit<OrderSiteAvailabilityRequest, 'priority'> {
  id: number;
  localStartAt: string;
  localEndAt: string;
  priority: number | SiteAvailabilityDraft['priority'] | null;
}
export const emptySiteAvailability = (
  source = 'Client'
): SiteAvailabilityDraft => ({
  date: '',
  startTime: '',
  endTime: '',
  timezoneId: 'UTC',
  duration: 60,
  priority: 'Preferred',
  status: true,
  sourceControlled: source,
});

export type SiteAvailabilityField =
  | 'date'
  | 'startTime'
  | 'endTime'
  | 'timezoneId'
  | 'duration'
  | 'window';

export class SiteAvailabilityValidationError extends Error {
  readonly windowIndex: number;
  readonly field: SiteAvailabilityField;

  constructor(
    message: string,
    windowIndex: number,
    field: SiteAvailabilityField
  ) {
    super(message);
    this.name = 'SiteAvailabilityValidationError';
    this.windowIndex = windowIndex;
    this.field = field;
  }
}

export const siteAvailabilityRequest = (
  row: SiteAvailabilityDraft,
  windowIndex = 0
): OrderSiteAvailabilityRequest => {
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(row.date) ||
    !/^\d{2}:\d{2}$/.test(row.startTime) ||
    !/^\d{2}:\d{2}$/.test(row.endTime)
  )
    throw new SiteAvailabilityValidationError(
      'Enter a date, start time and end time for each availability window.',
      windowIndex,
      !/^\d{4}-\d{2}-\d{2}$/.test(row.date)
        ? 'date'
        : !/^\d{2}:\d{2}$/.test(row.startTime)
        ? 'startTime'
        : 'endTime'
    );
  if (row.endTime <= row.startTime)
    throw new SiteAvailabilityValidationError(
      'End time must be after start time on the same date.',
      windowIndex,
      'endTime'
    );
  if (!row.timezoneId)
    throw new SiteAvailabilityValidationError(
      'Select a timezone.',
      windowIndex,
      'timezoneId'
    );
  if (
    !Number.isInteger(row.duration) ||
    row.duration < 15 ||
    row.duration > 720 ||
    row.duration % 15 !== 0
  )
    throw new SiteAvailabilityValidationError(
      'Duration must be 15–720 minutes in increments of 15.',
      windowIndex,
      'duration'
    );
  return {
    startAt: `${row.date}T${row.startTime}:00`,
    endAt: `${row.date}T${row.endTime}:00`,
    timezoneId: row.timezoneId,
    duration: row.duration,
    priority: row.priority,
    status: row.status,
    sourceControlled: row.sourceControlled,
    isAllDay: false,
  };
};
export const siteAvailabilityRequests = (rows: SiteAvailabilityDraft[]) => {
  const requests = rows.map((row, index) =>
    siteAvailabilityRequest(row, index)
  );
  for (const [index, a] of requests.entries()) {
    for (const [offset, b] of requests.slice(index + 1).entries()) {
      if (
        a.status &&
        b.status &&
        a.timezoneId === b.timezoneId &&
        a.startAt < b.endAt &&
        b.startAt < a.endAt
      )
        throw new SiteAvailabilityValidationError(
          'Active availability windows overlap. Adjust or remove the overlapping window.',
          index + 1 + offset,
          'window'
        );
    }
  }
  return requests;
};
export const siteAvailabilityDraft = (
  row: OrderSiteAvailabilityWindow
): SiteAvailabilityDraft => {
  // Server supplies local values in the window timezone; never reinterpret them in the browser timezone.
  const start =
    row.localStartAt || (row.timezoneId === 'UTC' ? row.startAt : '');
  const end = row.localEndAt || (row.timezoneId === 'UTC' ? row.endAt : '');
  if (!start || !end)
    throw new Error(
      'The availability API must be updated before editing these windows.'
    );
  return {
    id: row.id,
    date: start.slice(0, 10),
    startTime: start.slice(11, 16),
    endTime: end.slice(11, 16),
    timezoneId: row.timezoneId,
    duration: row.duration ?? 60,
    status: Boolean(row.status),
    sourceControlled: row.sourceControlled ?? '',
    priority:
      row.priority === 1 || row.priority === 'Preferred'
        ? 'Preferred'
        : row.priority === 2 || row.priority === 'SecondPreference'
        ? 'SecondPreference'
        : 'None',
  };
};
const endpoint = () => `${getBackendApiUrl()}/p`;
export const getOrderSiteAvailability = async (
  controller: AbortController,
  orderId: number
) => {
  const rows: OrderSiteAvailabilityWindow[] = [];
  for (let page = 1; ; page++) {
    const result: IPagedCollection<OrderSiteAvailabilityWindow> =
      await internalFetch(
        `${endpoint()}/order/${orderId}/orderSiteAvailabilityWindows?pageNo=${page}&pageSize=100&orderBy=startAt&code=${encodeURIComponent(
          getBackendApiKey() ?? ''
        )}`,
        'GET',
        controller
      );
    rows.push(...result.items);
    if (page >= result.pageCount || result.items.length === 0)
      return rows.map(siteAvailabilityDraft);
  }
};
export const saveOrderSiteAvailability = async (
  controller: AbortController,
  orderId: number,
  row: SiteAvailabilityDraft
): Promise<number> => {
  const result = await internalFetch(
    `${endpoint()}/orderSiteAvailabilityWindow${row.id ? `?id=${row.id}` : ''}`,
    row.id ? 'PUT' : 'POST',
    controller,
    JSON.stringify({ ...siteAvailabilityRequest(row), orderId }),
    Boolean(row.id),
    1
  );
  return row.id ?? result.id;
};
export const deleteOrderSiteAvailability = async (
  controller: AbortController,
  id: number
) => {
  await internalFetch(
    `${endpoint()}/orderSiteAvailabilityWindow?id=${id}`,
    'DELETE',
    controller,
    undefined,
    true,
    1
  );
};
